import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, Mail, Clock, CheckCircle, XCircle, AlertTriangle } from "lucide-react";
import { format } from "date-fns";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface ProfileCommunicationHistoryProps {
  profileId: string;
}

export function ProfileCommunicationHistory({ profileId }: ProfileCommunicationHistoryProps) {
  const { data: emails, isLoading, error } = useQuery({
    queryKey: ["profile_admin_emails", profileId],
    queryFn: async () => {
      console.log("[ProfileCommunicationHistory] Fetching emails for profile:", profileId);
      
      const { data, error } = await supabase
        .from("profile_admin_emails")
        .select(`
          id,
          subject,
          template_type,
          status,
          created_at,
          sent_by,
          profiles:sent_by(full_name)
        `)
        .eq("profile_id", profileId)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("[ProfileCommunicationHistory] Error fetching emails:", error);
        throw error;
      }
      
      console.log("[ProfileCommunicationHistory] Fetched emails:", data?.length ?? 0);
      return data;
    },
    enabled: !!profileId,
  });

  // Log any query errors
  if (error) {
    console.error("[ProfileCommunicationHistory] Query error:", error);
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "sent":
        return (
          <Badge variant="default" className="bg-success">
            <CheckCircle className="h-3 w-3 mr-1" />
            Sent
          </Badge>
        );
      case "failed":
        return (
          <Badge variant="destructive">
            <XCircle className="h-3 w-3 mr-1" />
            Failed
          </Badge>
        );
      case "queued":
        return (
          <Badge variant="secondary">
            <Clock className="h-3 w-3 mr-1" />
            Queued
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getTemplateLabel = (type: string | null) => {
    switch (type) {
      case "welcome_approved":
        return "Welcome Email";
      case "request_info":
        return "Info Request";
      case "manual":
        return "Manual";
      default:
        return type || "Unknown";
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-4">
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Failed to load communication history. Check console for details.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="py-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <Mail className="h-4 w-4" />
          Communication History
        </CardTitle>
      </CardHeader>
      <CardContent>
        {emails && emails.length > 0 ? (
          <div className="space-y-3">
            {emails.map((email) => (
              <div
                key={email.id}
                className="flex items-start justify-between p-3 border rounded-lg text-sm"
              >
                <div className="space-y-1">
                  <p className="font-medium">{email.subject}</p>
                  <div className="flex items-center gap-2 text-muted-foreground text-xs">
                    <span>{format(new Date(email.created_at), "MMM d, yyyy 'at' h:mm a")}</span>
                    <span>•</span>
                    <span>By {(email.profiles as any)?.full_name || "Admin"}</span>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  {getStatusBadge(email.status)}
                  <Badge variant="outline" className="text-xs">
                    {getTemplateLabel(email.template_type)}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-6 text-muted-foreground">
            <Mail className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No emails sent yet</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
